import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box } from "@mui/material";
import { useQuery } from "@tanstack/react-query";

import TableHead from "./TableHead";
import TableBody from "./TableBody";
import { PaginationWithLimit, ContainerWithLoader, FilterSearch } from "../index";

import newSortBy from "../../../utils/newSortBy";
import usePagination from "../../../hooks/usePagination";
import { StyledTable } from "../../../useGeneralStyles";
import { APIFunds } from "../../../api";
import { FORCED_TO_LIVE_ACCOUNTS_OPTIONS } from "../../../pages/FundedTraders/constants";
import { forcedToLiveAccountsActions } from "../../../reducers/forcedToLiveAccounts";
import { ActiveFilter } from "../FilterSearch";
import { FUNDS_API_URLS } from "../../../api/funds";

interface IProps {
  isGetAllAccounts?: boolean;
}
const ForcedToLive: React.FC<IProps> = ({ isGetAllAccounts }) => {
  const dispatch = useDispatch();

  const { isLoading, data } = useSelector((state) => state.forcedToLiveAccounts.accounts);
  const { limit, page, setLimit, setPage, count, setCount } = usePagination();

  const [sortBy, setSortBy] = useState({
    by: "id",
    order: false,
  });
  const [activeFilters, setActiveFilters] = useState<Record<string, ActiveFilter>>({});

  const handleSortBy = (by: string) => newSortBy({ by, sortBy, setSortBy });

  const { error, isError } = useQuery({
    queryKey: [FUNDS_API_URLS.get_ready_to_live_accounts, sortBy, limit, page, activeFilters],
    queryFn: async ({ signal }) => {
      dispatch(forcedToLiveAccountsActions.fetchAccountsRequest());
      const { data } = await APIFunds.getForcedToLiveAccountsRequest(
        {
          limit: +limit,
          page,
          sortBy,
          activeFilters,
        },
        signal
      );
      setCount(data.count);
      dispatch(forcedToLiveAccountsActions.fetchAccountsSuccess(data));
    },
  });
  useEffect(() => {
    if (isError && error) {
      dispatch(forcedToLiveAccountsActions.fetchAccountsFailed());
      console.error("Error fetching getWithdrawalsListRequest", error);
    }
  }, [isError, error]);

  return (
    <div>
      <Box mb="10px">
        <FilterSearch
          filterOptions={FORCED_TO_LIVE_ACCOUNTS_OPTIONS}
          handleSearch={setActiveFilters}
          initialFilters={[
            ...(isGetAllAccounts
              ? [
                  {
                    key: "withdrawals",
                    label: "Withdrawals",
                    value: "0",
                    subOption: { key: "more_than", label: "More than", displayLabel: "more than" },
                  },
                ]
              : []),
          ]}
        />
      </Box>

      <ContainerWithLoader isLoading={isLoading} report={data.rows}>
        <StyledTable>
          <TableHead sortBy={sortBy} handleSortBy={handleSortBy} />
          <TableBody isEditableWithdrawalAmount={isGetAllAccounts} />
        </StyledTable>
      </ContainerWithLoader>

      <PaginationWithLimit
        count={count}
        limit={limit}
        page={page}
        setLimit={setLimit}
        setPageState={setPage}
      />
    </div>
  );
};

export default ForcedToLive;
